/**
 * Format a spray job's display name with a duplicate counter.
 *
 * Previous behaviour also appended the date to the name — that's been removed
 * per stakeholder feedback: the dashboard now has a dedicated Date Created
 * column so we no longer want the date baked into the name. Duplicates are
 * detected purely on job_name, so refilled jobs (which share the original's
 * job_name) reliably get (1), (2), … suffixes.
 *
 * Convention:
 *   "Job Name"       — first/unique
 *   "Job Name (1)"   — first duplicate
 *   "Job Name (2)"   — second duplicate
 *
 * @param {Object} job - The spray job object
 * @param {Array} allJobs - All jobs in the same list (for duplicate detection)
 * @returns {string} Formatted display name
 */
export const formatSprayJobDisplayName = (job, allJobs = []) => {
  if (!job || !job.job_name) return '';

  const baseName = job.job_name;
  if (!allJobs || allJobs.length === 0) return baseName;

  const duplicates = allJobs.filter((j) => j && j.job_name === job.job_name);
  if (duplicates.length <= 1) return baseName;

  // Sort by created_at asc (fall back to id) so the original keeps the bare
  // name and refills/duplicates get incrementing suffixes.
  duplicates.sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    if (ta !== tb) return ta - tb;
    return (a.id || 0) - (b.id || 0);
  });
  const index = duplicates.findIndex((j) => j.id === job.id);
  return index <= 0 ? baseName : `${baseName} (${index})`;
};
